import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatErr, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Bike, Phone, Gauge } from "lucide-react";
import { toast } from "sonner";
import { PaginationBar, usePagination } from "@/components/Pagination";

const jobStatusColor = {
  open: "text-blue-400 bg-blue-500/10 border-blue-500/40",
  in_progress: "text-orange-400 bg-orange-500/10 border-orange-500/40",
  completed: "text-green-500 bg-green-500/10 border-green-500/40",
  delivered: "text-green-500 bg-green-500/10 border-green-500/40",
  cancelled: "text-red-500 bg-red-500/10 border-red-500/40",
};

export default function VehicleDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const pg2 = usePagination(data?.service_history || [], data);
  const pg = usePagination(data?.jobcards || [], data);

  useEffect(() => {
    api.get(`/vehicles/${id}/detail`).then(r => setData(r.data)).catch(e => toast.error(formatErr(e)));
  }, [id]);

  if (!data) return <div className="p-8 text-muted-foreground">Loading…</div>;
  const v = data.vehicle;

  return (
    <div className="p-8">
      <Button data-testid="back-to-vehicles" variant="ghost" size="sm" onClick={() => nav("/vehicles")} className="mb-4 text-xs uppercase tracking-wider">
        <ArrowLeft className="w-3 h-3 mr-1"/> Back to Vehicles
      </Button>

      <div className="border border-border bg-card rounded-sm p-5 mb-6">
        <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><Bike className="w-3 h-3"/> Vehicle Detail</div>
        <h1 className="font-display font-black text-4xl uppercase tracking-tighter" data-testid="veh-detail-reg">{v.registration}</h1>
        <div className="flex flex-wrap gap-x-6 gap-y-1 mt-3 text-xs text-muted-foreground">
          <span>{v.make} {v.model}{v.year ? ` · ${v.year}` : ""} · <span className="uppercase">{v.vehicle_type}</span></span>
          <span>Owner: <span className="text-foreground">{v.owner_name}</span></span>
          <span className="flex items-center gap-1"><Phone className="w-3 h-3"/> {v.owner_phone}</span>
          <span className="flex items-center gap-1"><Gauge className="w-3 h-3"/> Current Odometer: <span className="text-primary font-mono">{Number(v.odometer || 0).toLocaleString("en-IN")} km</span></span>
        </div>
      </div>

      <Tabs defaultValue="jobcards">
        <TabsList className="bg-secondary rounded-sm mb-4">
          <TabsTrigger data-testid="tab-jobcards" value="jobcards" className="text-xs uppercase tracking-wider">Job Card Details</TabsTrigger>
          <TabsTrigger data-testid="tab-history" value="history" className="text-xs uppercase tracking-wider">Service History</TabsTrigger>
        </TabsList>

        <TabsContent value="jobcards">
          <div className="border border-border bg-card rounded-sm overflow-hidden overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                <tr>
                  <th className="text-left p-3">Job Card #</th>
                  <th className="text-left p-3">Date</th>
                  <th className="text-left p-3">Mechanic</th>
                  <th className="text-left p-3">Complaints</th>
                  <th className="text-left p-3">Status</th>
                  <th className="text-right p-3">Invoice Amount</th>
                </tr>
              </thead>
              <tbody>
                {data.jobcards.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No job cards for this vehicle yet</td></tr>}
                {pg.pageItems.map(j => (
                  <tr key={j.id} data-testid={`veh-job-${j.id}`} className="border-t border-border hover:bg-secondary/50">
                    <td className="p-3 font-mono text-xs">
                      <Link data-testid={`veh-jobnum-${j.id}`} to={`/jobcards?open=${j.id}`} className="text-primary hover:underline">{j.job_number}</Link>
                    </td>
                    <td className="p-3 font-mono text-xs">{j.date}</td>
                    <td className="p-3 text-xs">{j.mechanic_name}</td>
                    <td className="p-3 text-xs max-w-64 truncate" title={j.complaints}>{j.complaints || "-"}</td>
                    <td className="p-3"><span className={`px-2 py-1 text-[10px] uppercase tracking-wider rounded-sm border ${jobStatusColor[j.status] || "text-muted-foreground bg-secondary border-border"}`}>{j.status}</span></td>
                    <td className="p-3 text-right font-mono text-xs">{j.invoice_amount != null ? inr(j.invoice_amount) : "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <PaginationBar pg={pg}/>
          </div>
        </TabsContent>

        <TabsContent value="history">
          <div className="border border-border bg-card rounded-sm overflow-hidden overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                <tr>
                  <th className="text-left p-3">Service Date</th>
                  <th className="text-right p-3">Odometer</th>
                  <th className="text-left p-3">Job Type</th>
                  <th className="text-left p-3">Work Done</th>
                  <th className="text-left p-3">Parts Used</th>
                  <th className="text-right p-3">Invoice Amount</th>
                </tr>
              </thead>
              <tbody>
                {data.service_history.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No service history yet</td></tr>}
                {pg2.pageItems.map((h, i) => (
                  <tr key={i} data-testid={`veh-hist-row-${i}`} className="border-t border-border hover:bg-secondary/50">
                    <td className="p-3 font-mono text-xs">{h.date}</td>
                    <td className="p-3 text-right font-mono text-xs">{h.odometer != null ? `${Number(h.odometer).toLocaleString("en-IN")} km` : "-"}</td>
                    <td className="p-3 text-xs">{h.job_type}</td>
                    <td className="p-3 text-xs max-w-64 truncate" title={h.work_done}>{h.work_done}</td>
                    <td className="p-3 text-xs max-w-56 truncate" title={h.parts_used}>{h.parts_used}</td>
                    <td className="p-3 text-right font-mono text-xs">{h.invoice_amount != null ? inr(h.invoice_amount) : "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <PaginationBar pg={pg2} testid="pagination-2"/>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
